import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DateRangePicker from "./DateRangePicker";
import { ConfigContext } from "../context/GlobalContext";
import * as actionType from '../context/actions';
import { Languages } from "../Utils/Languages";
import BusinessDropdown from "./child/BusinessDropdown";
import { Icon } from "@iconify/react";
import Currancy from "../Utils/currency.json";

const Breadcrumb = ({ title }) => {

  const { state, dispatch } = useContext(ConfigContext);
  const [activeFillter, setActiveFillter] = useState(state?.ACTIVEFILTER || "All")
  const [isOpenPicker, setIsOpenPicker] = useState(false);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [TransaltedFilter, setTransaltedFilter] = useState(Languages[state?.LANGUAGE]?.All);

  // ── Dashboard-only state ────────────────────────────────────────────────
  const isDashboard = title === Languages[state?.LANGUAGE]?.dashboard;

  const ownBusinesses = (state?.BUSINESSES || []).filter(b => !b.isShared);

  // Currency dropdown (dashboard only)
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target)) {
        setCurrencyOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Build unique currency list from invoices, proformas, quotations, expenses, and sale returns filtered by selected business
  const uniqueCurrencies = useMemo(() => {
    if (!isDashboard) return [];
    const invoices = state?.INVOICES || [];
    const proformas = state?.PROFORMAINVOICES || [];
    const quotations = state?.QUOTATION || [];
    const expenses = state?.EXPENSE || [];
    const saleReturns = state?.SALERETURN || [];
    
    const selectedBiz = state?.SELECTEDBUSINESS;
    const ownIds = new Set(ownBusinesses.map(b => String(b.companyId)));
    const hasSpecific = selectedBiz && selectedBiz !== "null" && ownIds.has(String(selectedBiz));

    const getFiltered = (items) => {
      return hasSpecific
        ? items.filter(item => String(item.companyId) === String(selectedBiz))
        : items.filter(item => ownIds.has(String(item.companyId)));
    };

    const set = new Set();
    getFiltered(invoices).forEach(item => { if (item?.currency) set.add(item.currency); });
    getFiltered(proformas).forEach(item => { if (item?.currency) set.add(item.currency); });
    getFiltered(quotations).forEach(item => { if (item?.currency) set.add(item.currency); });
    getFiltered(expenses).forEach(item => { if (item?.currency) set.add(item.currency); });
    getFiltered(saleReturns).forEach(item => { if (item?.currency) set.add(item.currency); });
    
    return Array.from(set);
  }, [
    isDashboard,
    state?.INVOICES,
    state?.PROFORMAINVOICES,
    state?.QUOTATION,
    state?.EXPENSE,
    state?.SALERETURN,
    state?.SELECTEDBUSINESS,
    state?.BUSINESSES
  ]);

  // Auto-select first currency when list changes
  useEffect(() => {
    if (!isDashboard) return;
    const fallback = state?.CURRENCYCODE || "INR";
    if (uniqueCurrencies.length > 0) {
      const current = state?.DASHBOARD_CURRENCY;
      if (!current || !uniqueCurrencies.includes(current)) {
        dispatch({ type: actionType.DASHBOARD_CURRENCY, payload: uniqueCurrencies[0] });
      }
    } else {
      if (state?.DASHBOARD_CURRENCY !== fallback) {
        dispatch({ type: actionType.DASHBOARD_CURRENCY, payload: fallback });
      }
    }
  }, [uniqueCurrencies, isDashboard]);

  const getCurrencyDisplay = (code) => {
    const matched = Currancy?.CountryCurrencyResponse?.find(c => c.currencyCode === code);
    if (matched) return `${matched.currencyCode} (${matched.currencytSymbol})`;
    return code || "";
  };

  const handleBusinessSelection = (businessId) => {
    dispatch({ type: actionType.SELECTEDBUSINESS, payload: businessId });
    // Reset currency so auto-select picks the first for the new business
    dispatch({ type: actionType.DASHBOARD_CURRENCY, payload: "" });
  };

  const HandleApply = (date) => {
    const selectedDate = date[0] ?? date[1];
    const updatedDate = [date[0] ?? selectedDate, date[1] ?? selectedDate];
    setIsOpenPicker(false);
    setDateRange(updatedDate);
    dispatch({ type: actionType.ACTIVEFILTER, payload: `Custome, ${new Date(updatedDate[0]).getTime()}, ${new Date(updatedDate[1]).getTime()}` });
  };

  const HandleCancel = () => {
    setIsOpenPicker(false);
    if (state?.ACTIVEFILTER?.includes("Custom")) {
      setActiveFillter("Custom");
    } else {
      setActiveFillter(state?.ACTIVEFILTER);
    }
  };

  useEffect(() => {
    if (activeFillter !== "Custom") {
      setDateRange([new Date(), new Date()]);
      dispatch({ type: actionType.ACTIVEFILTER, payload: activeFillter });
    }
  }, [activeFillter]);

  useEffect(() => {
    if (state?.ACTIVEPAGE !== title) {
      dispatch({ type: actionType.ACTIVEPAGE, payload: title });
      setActiveFillter("All");
    }
  }, [title]);

  // Theme helpers for currency dropdown
  const isDark = localStorage.getItem("theme") === "dark";
  const dropdownBg = isDark ? "#1b2431" : "#fff";
  const menuBorder = isDark ? "1px solid #334155" : "none";
  const textColor = isDark ? "#f1f5f9" : "#475569";
  const hoverBg = isDark ? "#334155" : "#F1F5F9";

  return (
    <>
      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
        <h6 className='fw-semibold mb-0'>{title}</h6>

        {(title != "Currency" && title != "Session" && title != Languages[state?.LANGUAGE]?.Session && title != "sessions" && title != "Payment" && title != "User Details" && title != "Referral Page") && (
          <div className="d-flex align-items-center flex-wrap gap-2">

            {/* ── Business Dropdown (dashboard only) ───────────────────── */}
            {isDashboard && ownBusinesses.length > 0 && (
              <BusinessDropdown
                businesses={ownBusinesses}
                selectedCompanyId={state?.SELECTEDBUSINESS}
                onBusinessChange={handleBusinessSelection}
                showAllOption={true}
              />
            )}

            {/* ── Currency Dropdown (dashboard only) ───────────────────── */}
            {isDashboard && uniqueCurrencies.length > 1 && (
              <div
                ref={currencyRef}
                className="dropdown custom-currency-dropdown"
                style={{ position: "relative" }}
              >
                <button
                  className="btn btn-outline-primary-600 not-active px-18 py-11 dropdown-toggle toggle-icon d-flex align-items-center justify-content-between gap-2 rounded"
                  type="button"
                  onClick={() => setCurrencyOpen(prev => !prev)}
                  style={{ minWidth: "165px", maxWidth: "210px", transition: "all 0.2s ease" }}
                >
                  <span style={{ fontSize: "14px", fontWeight: "500" }} className="text-truncate">
                    {state?.DASHBOARD_CURRENCY
                      ? getCurrencyDisplay(state.DASHBOARD_CURRENCY)
                      : "Currency"}
                  </span>
                </button>

                {currencyOpen && (
                  <ul
                    className="dropdown-menu show w-100 mt-1 shadow-lg rounded-8 text-start p-0"
                    style={{
                      zIndex: 1050,
                      minWidth: "100%",
                      position: "absolute",
                      left: 0,
                      backgroundColor: dropdownBg,
                      border: menuBorder
                    }}
                  >
                    <div
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                      className="hide-scrollbar"
                    >
                      {uniqueCurrencies.length === 0 ? (
                        <li
                          className="px-16 py-10 text-center small"
                          style={{ color: isDark ? "#94a3b8" : "#64748b" }}
                        >
                          No currencies found
                        </li>
                      ) : (
                        uniqueCurrencies.map(curr => {
                          const isSelected = curr === state?.DASHBOARD_CURRENCY;
                          return (
                            <li key={curr}>
                              <button
                                className={`dropdown-item px-16 py-8 rounded d-flex align-items-center gap-2 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${isSelected ? "bg-primary-50 fw-semibold text-primary-600" : ""}`}
                                onClick={() => {
                                  dispatch({ type: actionType.DASHBOARD_CURRENCY, payload: curr });
                                  setCurrencyOpen(false);
                                }}
                                style={{
                                  fontSize: "14px",
                                  color: isSelected ? "#487fff" : textColor,
                                  backgroundColor: isSelected
                                    ? (isDark ? "#1e293b" : "#f0f4ff")
                                    : "transparent"
                                }}
                                onMouseEnter={e =>
                                  (e.currentTarget.style.backgroundColor = hoverBg)
                                }
                                onMouseLeave={e =>
                                  (e.currentTarget.style.backgroundColor = isSelected
                                    ? (isDark ? "#1e293b" : "#f0f4ff")
                                    : "transparent")
                                }
                              >
                                <span className="flex-grow-1 text-truncate">
                                  {getCurrencyDisplay(curr)}
                                </span>
                                {isSelected && (
                                  <Icon
                                    icon="lucide:check"
                                    className="text-primary-600 text-lg ms-auto"
                                  />
                                )}
                              </button>
                            </li>
                          );
                        })
                      )}
                    </div>
                  </ul>
                )}
              </div>
            )}

            {/* ── Daywise filter ───────────────────────────────────────── */}
            <div className="dropdown">
              <button
                className="btn btn-outline-primary-600 not-active px-18 py-11 dropdown-toggle toggle-icon"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {" "}
                {activeFillter == "Custom"
                  ? `${new Date(dateRange[0]).toLocaleDateString()} - ${new Date(dateRange[1]).toLocaleDateString()}`
                  : TransaltedFilter}{" "}
              </button>
              <ul className="dropdown-menu">
                <li>
                  <Link
                    className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${activeFillter == "All" && "bg-primary-50"}`}
                    to="#"
                    onClick={() => { setActiveFillter("All"); setTransaltedFilter(Languages[state?.LANGUAGE]?.All) }}
                  >
                    {Languages[state?.LANGUAGE]?.All}
                  </Link>
                </li>
                <li>
                  <Link
                    className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${activeFillter == "Today" && "bg-primary-50"}`}
                    to="#"
                    onClick={() => { setActiveFillter("Today"); setTransaltedFilter(Languages[state?.LANGUAGE]?.today) }}
                  >
                    {Languages[state?.LANGUAGE]?.today}
                  </Link>
                </li>
                <li>
                  <Link
                    className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${activeFillter == "Yeasterday" && "bg-primary-50"}`}
                    to="#"
                    onClick={() => { setActiveFillter("Yeasterday"); setTransaltedFilter(Languages[state?.LANGUAGE]?.yesterday) }}
                  >
                    {Languages[state?.LANGUAGE]?.yesterday}
                  </Link>
                </li>
                <li>
                  <Link
                    className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${activeFillter == "Last 7 Days" && "bg-primary-50"}`}
                    to="#"
                    onClick={() => { setActiveFillter("Last 7 Days"); setTransaltedFilter(Languages[state?.LANGUAGE]?.last_7_days) }}
                  >
                    {Languages[state?.LANGUAGE]?.last_7_days}
                  </Link>
                </li>
                <li>
                  <Link
                    className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${activeFillter == "This Month" && "bg-primary-50"}`}
                    to="#"
                    onClick={() => { setActiveFillter("This Month"); setTransaltedFilter(Languages[state?.LANGUAGE]?.this_month) }}
                  >
                    {Languages[state?.LANGUAGE]?.this_month}
                  </Link>
                </li>
                <li>
                  <Link
                    className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${activeFillter == "Last Month" && "bg-primary-50"}`}
                    to="#"
                    onClick={() => { setActiveFillter("Last Month"); setTransaltedFilter(Languages[state?.LANGUAGE]?.last_month) }}
                  >
                    {Languages[state?.LANGUAGE]?.last_month}
                  </Link>
                </li>
                <li>
                  <Link
                    className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${activeFillter == "Custom" && "bg-primary-50"}`}
                    to="#"
                    onClick={() => {
                      setActiveFillter("Custom");
                      setIsOpenPicker(true);
                    }}
                  >
                    {Languages[state?.LANGUAGE]?.custome}
                  </Link>
                </li>
              </ul>
            </div>

          </div>
        )}
      </div>
      {isOpenPicker && <DateRangePicker isOpen={isOpenPicker} handleApply={HandleApply} handleCancel={HandleCancel} dates={dateRange} />}
    </>
  );
};

export default Breadcrumb;
